// components/DateFilter.tsx
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { IMaskInput } from "react-imask";

interface DateFilterProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onFilter: () => void;
  onClear: () => void;
  label?: string;
}

export const DateFilter = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onFilter,
  onClear,
  label,
}: DateFilterProps) => {
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);

  // Adicionar estados para controlar o texto dos inputs
  const [startInputValue, setStartInputValue] = useState(
    startDate ? format(startDate, "dd/MM/yyyy") : "",
  );
  const [endInputValue, setEndInputValue] = useState(
    endDate ? format(endDate, "dd/MM/yyyy") : "",
  );

  useEffect(() => {
    if (startDate) {
      setStartInputValue(format(startDate, "dd/MM/yyyy"));
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setEndInputValue(format(endDate, "dd/MM/yyyy"));
    }
  }, [endDate]);

  const handleDateChange = (
    type: "start" | "end",
    date: Date | undefined,
    setOpen: (open: boolean) => void,
  ) => {
    if (date && isValid(date)) {
      if (type === "start") {
        onStartDateChange(date);
        setStartInputValue(format(date, "dd/MM/yyyy"));
      } else {
        onEndDateChange(date);
        setEndInputValue(format(date, "dd/MM/yyyy"));
      }
      setOpen(false);
    }
  };

  const handleInputChange = (type: "start" | "end", value: string) => {
    // Sempre atualizar o valor visível do input
    if (type === "start") {
      setStartInputValue(value);
    } else {
      setEndInputValue(value);
    }

    const digitsOnly = value.replace(/\D/g, "");

    // Atualizar o estado da data apenas quando tivermos todos os 8 dígitos e a data for válida
    if (digitsOnly.length === 8) {
      const parsedDate = parse(value, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) {
        if (type === "start") {
          onStartDateChange(parsedDate);
        } else {
          onEndDateChange(parsedDate);
        }
      }
    } else if (value === "") {
      // Limpar a data se o valor estiver vazio
      if (type === "start") {
        onStartDateChange(undefined);
      } else {
        onEndDateChange(undefined);
      }
    }
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            {label || "Filtro por data"}
          </Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClear();
              setStartInputValue("");
              setEndInputValue("");
            }}
          >
            Limpar
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Data Inicial */}
          <div className="md:col-span-5">
            <Popover
              open={startCalendarOpen}
              onOpenChange={setStartCalendarOpen}
            >
              <div className="flex border rounded-md">
                <div className="relative grow">
                  <PopoverTrigger asChild>
                    <div className="cursor-pointer">
                      <IMaskInput
                        mask="00/00/0000"
                        value={startInputValue}
                        onAccept={(value: string) =>
                          handleInputChange("start", value)
                        }
                        className="bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2 cursor-pointer"
                        placeholder="dd/mm/aaaa"
                        onClick={() => setStartCalendarOpen(true)}
                      />
                    </div>
                  </PopoverTrigger>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-l-none"
                  onClick={() => setStartCalendarOpen(true)}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </div>

              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  locale={ptBR}
                  mode="single"
                  selected={startDate}
                  defaultMonth={startDate || new Date()}
                  onSelect={(date) =>
                    handleDateChange("start", date, setStartCalendarOpen)
                  }
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Data Final */}
          <div className="md:col-span-5">
            <Popover open={endCalendarOpen} onOpenChange={setEndCalendarOpen}>
              <div className="flex border rounded-md">
                <div className="relative grow">
                  <PopoverTrigger asChild>
                    <div className="cursor-pointer">
                      <IMaskInput
                        mask="00/00/0000"
                        value={endInputValue}
                        onAccept={(value: string) =>
                          handleInputChange("end", value)
                        }
                        className="bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2 cursor-pointer"
                        placeholder="dd/mm/aaaa"
                        onClick={() => setEndCalendarOpen(true)}
                      />
                    </div>
                  </PopoverTrigger>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-l-none"
                  onClick={() => setEndCalendarOpen(true)}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </div>

              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  locale={ptBR}
                  mode="single"
                  selected={endDate}
                  defaultMonth={endDate || new Date()}
                  onSelect={(date) =>
                    handleDateChange("end", date, setEndCalendarOpen)
                  }
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Botão Filtrar */}
          <div className="md:col-span-2 flex items-end">
            <Button
              onClick={onFilter}
              className="w-full bg-primary-500 text-white"
              size="default"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
